/**
 * SOS+ Emergency Mode (PROJECT_BRIEF §22.7).
 *
 * Two surfaces:
 *
 * 1. `<EmergencyModeBanner />` — sticky, dismissible banner rendered at the
 *    top of every authenticated route via DashboardLayout. Shows only when
 *    at least one unresolved SOS exists. WS-driven (sos.triggered events
 *    immediately invalidate the active-sos query); a polling fallback runs
 *    every 15 s in case the socket is down.
 *
 * 2. `<EmergencyModeDialog />` — full-screen modal with the rich live
 *    snapshot: child name, last reported lat/lng, freshness, battery,
 *    speed, one-tap "Open in maps", "Notify trusted circle", and the
 *    existing Acknowledge / Resolve actions.
 *
 * The modal auto-opens when a *new* unresolved SOS appears. Once the
 * parent dismisses it, it stays closed until either (a) a brand-new SOS
 * arrives, or (b) the parent re-opens it from the banner.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertOctagon,
  BatteryLow,
  Bell,
  CheckCircle2,
  MapPinned,
  PhoneCall,
  Siren,
  Wifi,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { locationsApi, type SosLiveSnapshot } from '@/api/locations';
import { usersApi } from '@/api/users';

function freshnessLabel(seconds: number): string {
  if (seconds < 30) return 'Live';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  return `${Math.round(seconds / 3600)}h ago`;
}

function freshnessTone(seconds: number): 'fresh' | 'stale' | 'cold' {
  if (seconds < 90) return 'fresh';
  if (seconds < 600) return 'stale';
  return 'cold';
}

function useActiveSos() {
  // Polling-only for now. The shared WebSocket lives in MapPage; rather
  // than double-connecting from the always-mounted banner, we lean on a
  // tight refetch interval (8s) so a new SOS surfaces within ~one breath.
  // A future refactor can lift the WS into a context provider in
  // DashboardLayout and switch this back to event-driven invalidation.
  return useQuery({
    queryKey: ['sos', 'active'],
    queryFn: () => locationsApi.activeSos(),
    refetchInterval: 8_000,
    retry: false,
  });
}

/**
 * Sticky banner. Hidden when there's no active SOS. Click → opens dialog.
 */
export function EmergencyModeBanner() {
  const q = useActiveSos();
  const [open, setOpen] = useState(false);
  const lastSeenId = useRef<string | null>(null);

  const active = q.data ?? [];
  const head = active[0];

  // Auto-open the dialog the first time a *new* SOS appears. We track the
  // last SOS id we surfaced so a parent who closes the modal doesn't get
  // re-popped on every poll; only a brand-new SOS reopens it.
  useEffect(() => {
    if (!head) return;
    if (lastSeenId.current === head.sos.id) return;
    lastSeenId.current = head.sos.id;
    setOpen(true);
  }, [head]);

  if (!head) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'sticky top-0 z-40 flex w-full items-center gap-3 border-b px-4 py-2.5 text-left text-sm shadow-sm transition-colors',
          'border-rose-700 bg-rose-600 text-white hover:bg-rose-700',
        )}
      >
        <span className="grid size-7 shrink-0 animate-pulse place-items-center rounded-full bg-white/15">
          <Siren className="size-4" />
        </span>
        <span className="flex-1 truncate font-semibold">
          {active.length === 1
            ? 'SOS active — tap to open Emergency Mode'
            : `${active.length} SOS events active — tap to review`}
        </span>
        <span className="hidden text-xs uppercase tracking-wider opacity-80 sm:inline">
          Live
        </span>
      </button>
      <EmergencyModeDialog
        open={open}
        onClose={() => setOpen(false)}
        snapshots={active}
      />
    </>
  );
}

interface DialogProps {
  open: boolean;
  onClose: () => void;
  snapshots: SosLiveSnapshot[];
}

function EmergencyModeDialog({ open, onClose, snapshots }: DialogProps) {
  const qc = useQueryClient();
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveNote, setResolveNote] = useState('');

  // We focus on the newest SOS; if multiple, the banner shows count + the
  // dialog lets the parent step through. For MVP we just render the head.
  const snap = snapshots[0] ?? null;
  const children = useQuery({ queryKey: ['children'], queryFn: () => usersApi.listChildren(), enabled: open });
  const child = useMemo(
    () => (snap ? children.data?.find((c) => c.id === snap.child_id) : null),
    [children.data, snap],
  );

  const ack = useMutation({
    mutationFn: (id: string) => locationsApi.ackSos(id),
    onSuccess: () => {
      toast.success('Acknowledged — the child sees that help is coming.');
      void qc.invalidateQueries({ queryKey: ['sos', 'active'] });
      void qc.invalidateQueries({ queryKey: ['sos'] });
    },
    onError: () => toast.error('Could not acknowledge.'),
  });

  const escalate = useMutation({
    mutationFn: (id: string) => locationsApi.notifyCircle(id),
    onSuccess: () => {
      toast.success('Trusted circle has been notified.');
      void qc.invalidateQueries({ queryKey: ['sos', 'active'] });
    },
    onError: () => toast.error('Could not notify circle.'),
  });

  const resolve = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      locationsApi.resolveSos(id, note || undefined),
    onSuccess: () => {
      toast.success('SOS resolved.');
      setResolveOpen(false);
      setResolveNote('');
      onClose();
      void qc.invalidateQueries({ queryKey: ['sos', 'active'] });
      void qc.invalidateQueries({ queryKey: ['sos'] });
    },
    onError: () => toast.error('Could not resolve.'),
  });

  if (!snap) return null;

  const lat = snap.position?.lat ?? snap.sos.lat ?? null;
  const lng = snap.position?.lng ?? snap.sos.lng ?? null;
  const freshness = snap.position?.freshness_seconds ?? null;
  const tone = freshness != null ? freshnessTone(freshness) : 'cold';
  const triggered = new Date(snap.sos.triggered_at);

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent className="max-w-lg border-rose-200">
          <DialogHeader>
            <div className="mb-2 flex items-center gap-2">
              <span className="grid size-9 place-items-center rounded-full bg-rose-100 text-rose-700">
                <AlertOctagon className="size-5" />
              </span>
              <div>
                <DialogTitle className="text-lg">Emergency Mode</DialogTitle>
                <DialogDescription className="text-xs">
                  {child?.name ?? 'A child'} triggered SOS · {triggered.toLocaleString()}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid grid-cols-3 gap-2 rounded-card border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-sunken)] p-3 text-center text-xs">
              <div>
                <p className="font-semibold uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
                  Signal
                </p>
                <p
                  className={cn(
                    'mt-0.5 inline-flex items-center gap-1 font-medium',
                    tone === 'fresh' && 'text-emerald-600',
                    tone === 'stale' && 'text-amber-600',
                    tone === 'cold' && 'text-rose-600',
                  )}
                >
                  <Wifi className="size-3.5" />
                  {freshness != null ? freshnessLabel(freshness) : 'No signal'}
                </p>
              </div>
              <div>
                <p className="font-semibold uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
                  Battery
                </p>
                <p className="mt-0.5 inline-flex items-center gap-1 font-medium">
                  {snap.position?.battery_pct != null && snap.position.battery_pct < 20 && (
                    <BatteryLow className="size-3.5 text-rose-600" />
                  )}
                  {snap.position?.battery_pct != null
                    ? `${snap.position.battery_pct}%`
                    : 'Unknown'}
                </p>
              </div>
              <div>
                <p className="font-semibold uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
                  Speed
                </p>
                <p className="mt-0.5 font-medium">
                  {snap.position?.speed_mps != null
                    ? `${(snap.position.speed_mps * 3.6).toFixed(0)} km/h`
                    : '—'}
                </p>
              </div>
            </div>

            <div className="rounded-card border border-[color:var(--kz-color-border-subtle)] p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
                Last known location
              </p>
              {lat != null && lng != null ? (
                <>
                  <p className="mt-1 font-mono text-sm">
                    {lat.toFixed(5)}, {lng.toFixed(5)}
                  </p>
                  <Link
                    to={`/map?lat=${lat}&lng=${lng}&focus=${snap.child_id}`}
                    onClick={() => onClose()}
                    className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-[color:var(--kz-color-brand-primary)] hover:underline"
                  >
                    <MapPinned className="size-3.5" /> Open on map
                  </Link>
                </>
              ) : (
                <p className="mt-1 text-sm italic text-[color:var(--kz-color-ink-muted)]">
                  No location available yet.
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Button
                variant="outline"
                onClick={() => ack.mutate(snap.sos.id)}
                disabled={!!snap.sos.acknowledged_at || ack.isPending}
              >
                <CheckCircle2 className="size-4" />
                {snap.sos.acknowledged_at ? 'Acknowledged' : 'Acknowledge'}
              </Button>
              <Button
                variant="outline"
                onClick={() => escalate.mutate(snap.sos.id)}
                disabled={snap.escalated || escalate.isPending}
              >
                <Bell className="size-4" />
                {snap.escalated ? 'Circle notified' : 'Notify trusted circle'}
              </Button>
              <Button variant="outline" disabled>
                <PhoneCall className="size-4" />
                Call child (mobile only)
              </Button>
              <Button
                className="bg-rose-600 hover:bg-rose-700"
                onClick={() => setResolveOpen(true)}
                disabled={resolve.isPending}
              >
                <X className="size-4" />
                Resolve & close
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resolveOpen} onOpenChange={(v) => { if (!v) setResolveOpen(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Resolve SOS</DialogTitle>
            <DialogDescription>
              Add a short note so the family knows what happened.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="resolve-note">Note (optional)</Label>
            <Input
              id="resolve-note"
              placeholder="e.g. false alarm — kid tested the button"
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              maxLength={500}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResolveOpen(false)} disabled={resolve.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => resolve.mutate({ id: snap.sos.id, note: resolveNote.trim() })}
              disabled={resolve.isPending}
            >
              {resolve.isPending ? 'Resolving…' : 'Mark resolved'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
